import { useCallback, useEffect, useRef, useState } from "react";
import {
  SelectFieldOption,
  SelectFieldPageLazyResult,
  SelectFieldOffsetLazyResult,
  SelectFieldGroupLazyResult,
  SelectFieldPropsLazyPage,
  SelectFieldPropsLazyOffset,
  SelectFieldPropsLazyGroup,
  SelectFieldPropsEager,
} from "../../../beta/components/SelectField/types";

export const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_CACHE_MAX_SIZE = 15;

/**
 * Configuration for caching search results in useSelectFieldLoading.
 * When enabled, the full accumulated state (options, pagination cursor, hasMore)
 * is cached per search value, allowing instant restoration when returning to
 * a previous search.
 */
export type CacheConfig = {
  /**
   * Whether caching is enabled. Defaults to true.
   */
  enabled?: boolean;
  /**
   * Maximum number of search values to cache before clearing.
   * Defaults to 15.
   */
  maxSize?: number;
};

/**
 * The full state cached per search value, including accumulated options,
 * pagination cursor, and hasMore flag.
 */
type CachedSearchState = {
  options: SelectFieldOption[];
  hasMore: boolean;
  previousPage: number;
  previousOffset: number;
  previousGroup: string | number | null;
};

type UseSelectFieldLoadingConfigLazyPage = Pick<
  SelectFieldPropsLazyPage,
  "lazy" | "loadOptions"
> & {
  pageSize: number;
  cache?: CacheConfig;
};

type UseSelectFieldLoadingConfigLazyOffset = Pick<
  SelectFieldPropsLazyOffset,
  "lazy" | "loadOptions"
> & {
  limit: number;
  cache?: CacheConfig;
};

type UseSelectFieldLoadingConfigLazyGroup = Pick<
  SelectFieldPropsLazyGroup,
  "lazy" | "loadOptions"
> & {
  cache?: CacheConfig;
};

type UseSelectFieldLoadingConfigEager = Pick<
  SelectFieldPropsEager,
  "lazy" | "loadOptions"
> & {
  cache?: CacheConfig;
};

export type UseSelectFieldLoadingConfig =
  | UseSelectFieldLoadingConfigLazyPage
  | UseSelectFieldLoadingConfigLazyOffset
  | UseSelectFieldLoadingConfigLazyGroup
  | UseSelectFieldLoadingConfigEager;

export type UseSelectFieldLoadingReturn = {
  /**
   * Current loaded options array
   */
  options: SelectFieldOption[];
  /**
   * Boolean indicating initial load in progress
   */
  loading: boolean;
  /**
   * Boolean indicating load more in progress
   */
  loadingMore: boolean;
  /**
   * Boolean indicating if more options are available
   */
  hasMore: boolean;
  /**
   * Function to load options. When initial is true, replaces options and sets loading state.
   * When initial is false or undefined, appends options and sets loadingMore state.
   * @param searchValue - The search value to load options for
   * @param options - Optional configuration
   * @param options.initial - Whether this is an initial load (replaces options) or continuation (appends options)
   */
  loadOptions: (
    searchValue: string,
    options?: { initial?: boolean },
  ) => Promise<void>;
  /**
   * Function to load more options (for pagination). Appends to existing options.
   * @param searchValue - The search value to load more options for
   */
  loadMore: (searchValue: string) => Promise<void>;
  /**
   * Function to reset all state to initial values
   */
  reset: () => void;
  /**
   * Function to clear the search results cache
   */
  clearCache: () => void;
};

/**
 * Custom hook for managing SelectField option loading state and logic.
 *
 * Features:
 * - Manages option loading state (loading, loadingMore, hasMore)
 * - Supports eager loading and three lazy loading modes (page, offset, group)
 * - Handles pagination state tracking for lazy loading
 * - Provides methods to load options (initial or continuation) and load more
 * - Automatically manages option replacement vs appending based on load type
 * - Tracks pagination state (page, offset, group) for lazy loading modes
 * - Provides reset functionality to clear all state
 *
 * @param config - Configuration object containing lazy mode, loadOptions function, and pagination settings
 * @returns Object containing options, loading states, and load functions
 *
 * @example
 * const { options, loading, loadingMore, hasMore, loadOptions, loadMore } = useSelectFieldLoading({
 *   lazy: "page",
 *   loadOptions: async (searchValue, page, pageSize) => {
 *     const result = await fetchOptions(searchValue, page, pageSize);
 *     return { options: result.items, hasMore: result.hasMore };
 *   },
 *   pageSize: 10
 * });
 */
export function useSelectFieldLoading(
  config: UseSelectFieldLoadingConfig,
): UseSelectFieldLoadingReturn {
  const { lazy, loadOptions: loadOptionsProp, cache: cacheConfig } = config;

  // Cache configuration
  const cacheEnabled = cacheConfig?.enabled !== false;
  const cacheMaxSize = cacheConfig?.maxSize ?? DEFAULT_CACHE_MAX_SIZE;

  const [options, setOptions] = useState<SelectFieldOption[]>([]);
  const [hasMoreToLoad, setHasMoreToLoad] = useState(false);
  const [previousGroup, setPreviousGroup] = useState<string | number | null>(
    null,
  );
  const [previousPage, setPreviousPage] = useState<number>(0);
  const [previousOffset, setPreviousOffset] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Cache state: stores full accumulated state per search value
  const [stateCache, setStateCache] = useState<Map<string, CachedSearchState>>(
    () => new Map(),
  );
  // Track the current search value to know which cache entry to update on loadMore
  const currentSearchValueRef = useRef<string>("");
  // Track whether loadOptions has completed at least once, to distinguish
  // the initial empty state from an API response that returned zero results.
  const hasCompletedLoadRef = useRef(false);

  const appendOptions = useCallback((newOptions: SelectFieldOption[]) => {
    setOptions((prevOptions) => prevOptions.concat(newOptions));
  }, []);

  const pageSize =
    lazy === "page"
      ? (config as UseSelectFieldLoadingConfigLazyPage).pageSize
      : DEFAULT_PAGE_SIZE;
  const limit =
    lazy === "offset"
      ? (config as UseSelectFieldLoadingConfigLazyOffset).limit
      : DEFAULT_PAGE_SIZE;

  // Helper to restore state from cache
  const restoreFromCache = useCallback((cached: CachedSearchState) => {
    setOptions(cached.options);
    setHasMoreToLoad(cached.hasMore);
    setPreviousPage(cached.previousPage);
    setPreviousOffset(cached.previousOffset);
    setPreviousGroup(cached.previousGroup);
  }, []);

  // Sync current state to cache whenever options or pagination state changes.
  // This effect runs after React has batched all state updates from loadOptions/loadMore,
  // ensuring we cache the complete, up-to-date state.
  useEffect(() => {
    if (!cacheEnabled) return;
    const searchValue = currentSearchValueRef.current;
    // Don't cache the initial empty state before any load has completed
    if (!hasCompletedLoadRef.current) return;

    setStateCache((prev) => {
      // Clear cache if we've reached max size
      if (prev.size >= cacheMaxSize) {
        prev.clear();
      }

      prev.set(searchValue, {
        options,
        hasMore: hasMoreToLoad,
        previousPage,
        previousOffset,
        previousGroup,
      });
      return new Map(prev);
    });
  }, [
    cacheEnabled,
    cacheMaxSize,
    options,
    hasMoreToLoad,
    previousPage,
    previousOffset,
    previousGroup,
  ]);

  const loadOptions = useCallback(
    async (
      searchValue: string,
      options?: { initial?: boolean },
    ): Promise<void> => {
      const isInitial = !!options?.initial;

      // On initial load, check cache first
      if (isInitial && cacheEnabled) {
        const cached = stateCache.get(searchValue);
        if (cached) {
          // Restore full state from cache - no API call needed
          currentSearchValueRef.current = searchValue;
          restoreFromCache(cached);
          return;
        }
      }

      // Track current search value for cache updates on loadMore
      if (isInitial) {
        currentSearchValueRef.current = searchValue;
      }

      (isInitial ? setLoading : setLoadingMore)(true);

      try {
        if (!lazy) {
          const result = await loadOptionsProp(searchValue);
          const resolved = Array.isArray(result) ? result : await result;
          (isInitial ? setOptions : appendOptions)(resolved);
        } else {
          let result:
            | SelectFieldPageLazyResult
            | SelectFieldOffsetLazyResult
            | SelectFieldGroupLazyResult;

          if (lazy === "page") {
            const page = isInitial ? 0 : previousPage;
            result = await loadOptionsProp(searchValue, page, pageSize);
            setPreviousPage(isInitial ? 1 : page + 1);
          } else if (lazy === "offset") {
            const offset = isInitial ? 0 : previousOffset;
            result = await loadOptionsProp(searchValue, offset, limit);
            setPreviousOffset(isInitial ? limit : offset + limit);
          } else if (lazy === "group") {
            const group = isInitial ? null : previousGroup;
            result = await loadOptionsProp(searchValue, group);
            setPreviousGroup(
              result.options[result.options.length - 1]?.group ?? null,
            );
          } else {
            throw new Error(
              `Invalid lazy mode: ${lazy}. Expected 'page', 'offset', or 'group'.`,
            );
          }

          setHasMoreToLoad(result.hasMore ?? false);
          (isInitial ? setOptions : appendOptions)(result.options);
        }
        hasCompletedLoadRef.current = true;
      } finally {
        (isInitial ? setLoading : setLoadingMore)(false);
      }
    },
    [
      lazy,
      pageSize,
      limit,
      loadOptionsProp,
      appendOptions,
      previousPage,
      previousOffset,
      previousGroup,
      cacheEnabled,
      stateCache,
      restoreFromCache,
    ],
  );

  const loadMore = useCallback(
    async (searchValue: string): Promise<void> => {
      await loadOptions(searchValue, { initial: false });
    },
    [loadOptions],
  );

  const reset = useCallback(() => {
    setOptions([]);
    setHasMoreToLoad(false);
    setPreviousGroup(null);
    setPreviousPage(0);
    setPreviousOffset(0);
    setLoading(false);
    setLoadingMore(false);
  }, []);

  const clearCache = useCallback(() => {
    setStateCache(new Map());
  }, []);

  return {
    options,
    loading,
    loadingMore,
    hasMore: hasMoreToLoad,
    loadOptions,
    loadMore,
    reset,
    clearCache,
  };
}
